import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, BadgePercent, Plus, X } from 'lucide-react';
import { TcCouponService } from '../../services/tcCouponService';

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const fmtDate = (value) => {
  if (!value) return 'No expiry';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const EMPTY_FORM = {
  code: '',
  discount_type: 'percent',
  discount_value: '10',
  applies_to_plan_slug: 'all',
  max_uses: '',
  valid_until: '',
  is_active: true,
};

const TABS = [
  { id: 'promo', label: 'Promo Coupons' },
  { id: 'tc', label: 'TC Tracking' },
];

export default function Coupons() {
  const [tab, setTab] = useState('promo');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  const [promoRows, setPromoRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [promoSearch, setPromoSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [tcRows, setTcRows] = useState([]);
  const [tcSearch, setTcSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [promo, planList, tc] = await Promise.all([
        TcCouponService.listPromoCoupons(),
        TcCouponService.listMembershipPlans(),
        TcCouponService.listAdminStats(),
      ]);
      setPromoRows(promo);
      setPlans(planList);
      setTcRows(tc);
    } catch (e) {
      setError(e.message || 'Could not load coupons');
      setPromoRows([]);
      setTcRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredPromo = useMemo(() => {
    const q = promoSearch.trim().toLowerCase();
    if (!q) return promoRows;
    return promoRows.filter((row) => {
      const hay = [row.code, row.applies_to_plan_slug, row.discount_type]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [promoRows, promoSearch]);

  const filteredTc = useMemo(() => {
    const q = tcSearch.trim().toLowerCase();
    if (!q) return tcRows;
    return tcRows.filter((row) => {
      const hay = [
        row.telecaller_name,
        row.telecaller_phone,
        row.yearly_code,
        row.monthly_code,
        row.universal_code,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [tcRows, tcSearch]);

  const tcTotals = useMemo(() => {
    return filteredTc.reduce(
      (acc, row) => {
        acc.yearly += Number(row.yearly_uses || 0);
        acc.monthly += Number(row.monthly_uses || 0);
        acc.universal += Number(row.universal_uses || 0);
        acc.paid += Number(row.paid_subscriptions || 0);
        acc.redemptions += Number(row.total_redemptions || 0);
        return acc;
      },
      { yearly: 0, monthly: 0, universal: 0, paid: 0, redemptions: 0 },
    );
  }, [filteredTc]);

  const promoStats = useMemo(() => {
    const active = promoRows.filter((r) => r.is_active).length;
    const uses = promoRows.reduce((s, r) => s + Number(r.uses_count || 0), 0);
    return { total: promoRows.length, active, uses };
  }, [promoRows]);

  const planLabel = (slug) => {
    if (!slug || slug === 'all') return 'All plans';
    const p = plans.find((x) => x.slug === slug);
    return p ? `${p.name} (${slug})` : slug;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setFlash('');
    try {
      await TcCouponService.createPromoCoupon({
        ...form,
        applies_to_plan_slug: form.applies_to_plan_slug === 'all' ? 'all' : form.applies_to_plan_slug || null,
      });
      setFlash(`Coupon ${form.code.trim().toUpperCase()} created`);
      setForm(EMPTY_FORM);
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err.message || 'Could not create coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    setTogglingId(row.id);
    setError('');
    try {
      await TcCouponService.setPromoCouponActive(row.id, !row.is_active);
      await load();
    } catch (err) {
      setError(err.message || 'Could not update coupon');
    } finally {
      setTogglingId(null);
    }
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil).getTime() <= Date.now();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BadgePercent className="text-indigo-600" size={28} />
            Coupons
          </h1>
          <p className="text-gray-600 mt-1 max-w-2xl">
            Create time-based promo codes for campaigns, and track telecaller RSA coupons.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          {tab === 'promo' ? (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
            >
              <Plus size={16} />
              Create Coupon
            </button>
          ) : null}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded-xl bg-red-50 text-red-800 px-4 py-3 text-sm ring-1 ring-red-200">{error}</div>
      ) : null}
      {flash ? (
        <div className="rounded-xl bg-emerald-50 text-emerald-800 px-4 py-3 text-sm ring-1 ring-emerald-200">{flash}</div>
      ) : null}

      {tab === 'promo' ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Promo coupons" value={fmtNum(promoStats.total)} />
            <StatCard label="Active" value={fmtNum(promoStats.active)} />
            <StatCard label="Total uses" value={fmtNum(promoStats.uses)} />
          </div>

          <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Promo / Campaign Coupons</h2>
                <p className="text-sm text-gray-500">
                  Time-based codes for promotions (not linked to telecallers)
                </p>
              </div>
              <div className="relative w-full sm:w-72">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={promoSearch}
                  onChange={(e) => setPromoSearch(e.target.value)}
                  placeholder="Search code…"
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                />
              </div>
            </div>

            {loading ? (
              <div className="px-4 py-16 text-center text-gray-500 text-sm">Loading promo coupons…</div>
            ) : filteredPromo.length === 0 ? (
              <div className="px-4 py-16 text-center text-gray-500 text-sm">
                {promoRows.length === 0
                  ? 'No promo coupons yet. Click Create Coupon to add one.'
                  : 'No matches for your search.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-4 py-3 font-medium">Code</th>
                      <th className="px-4 py-3 font-medium">Discount</th>
                      <th className="px-4 py-3 font-medium">Applies to</th>
                      <th className="px-4 py-3 font-medium">Valid until</th>
                      <th className="px-4 py-3 font-medium text-right">Uses</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredPromo.map((row) => {
                      const expired = isExpired(row.valid_until);
                      return (
                        <tr key={row.id} className="hover:bg-gray-50/80">
                          <td className="px-4 py-3 font-mono font-semibold text-gray-900">{row.code}</td>
                          <td className="px-4 py-3 text-gray-800">
                            {row.discount_type === 'percent'
                              ? `${fmtNum(row.discount_value)}%`
                              : `₹${fmtNum(row.discount_value)}`}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{planLabel(row.applies_to_plan_slug)}</td>
                          <td className="px-4 py-3 text-gray-700">
                            <span className={expired ? 'text-red-600' : ''}>{fmtDate(row.valid_until)}</span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                            {fmtNum(row.uses_count)}
                            {row.max_uses != null ? ` / ${fmtNum(row.max_uses)}` : ''}
                          </td>
                          <td className="px-4 py-3">
                            {!row.is_active ? (
                              <StatusBadge tone="gray">Inactive</StatusBadge>
                            ) : expired ? (
                              <StatusBadge tone="red">Expired</StatusBadge>
                            ) : (
                              <StatusBadge tone="green">Active</StatusBadge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              disabled={togglingId === row.id}
                              onClick={() => handleToggle(row)}
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                            >
                              {row.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Telecallers" value={fmtNum(filteredTc.length)} />
            <StatCard label="Total redemptions" value={fmtNum(tcTotals.redemptions)} />
            <StatCard label="Paid subscriptions" value={fmtNum(tcTotals.paid)} />
            <StatCard
              label="Uses (Y / M / U)"
              value={`${fmtNum(tcTotals.yearly)} / ${fmtNum(tcTotals.monthly)} / ${fmtNum(tcTotals.universal)}`}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">TC RSA Coupon Tracking</h2>
                <p className="text-sm text-gray-500">Yearly, monthly &amp; universal codes per telecaller</p>
              </div>
              <div className="relative w-full sm:w-72">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={tcSearch}
                  onChange={(e) => setTcSearch(e.target.value)}
                  placeholder="Search name, phone, code…"
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                />
              </div>
            </div>

            {loading ? (
              <div className="px-4 py-16 text-center text-gray-500 text-sm">Loading coupon stats…</div>
            ) : filteredTc.length === 0 ? (
              <div className="px-4 py-16 text-center text-gray-500 text-sm">
                {tcRows.length === 0 ? 'No telecaller coupon data yet.' : 'No matches for your search.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-4 py-3 font-medium whitespace-nowrap">Telecaller</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap">Yearly code</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Yearly uses</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap">Monthly code</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Monthly uses</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap">Universal code</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Universal uses</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Paid subs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTc.map((row) => (
                      <tr key={row.telecaller_id} className="hover:bg-gray-50/80">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{row.telecaller_name || '—'}</div>
                          {row.telecaller_phone ? (
                            <div className="text-xs text-gray-500 mt-0.5">{row.telecaller_phone}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-800">{row.yearly_code || '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtNum(row.yearly_uses)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-800">{row.monthly_code || '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtNum(row.monthly_uses)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-800">{row.universal_code || '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtNum(row.universal_uses)}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-indigo-600">
                          {fmtNum(row.paid_subscriptions)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showCreate ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Create promo coupon</h3>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="px-5 py-4 space-y-4">
              <Field label="Coupon code">
                <input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. HOLI50"
                  className="field-input font-mono"
                  maxLength={32}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Discount type">
                  <select
                    value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="field-input"
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed (₹)</option>
                  </select>
                </Field>
                <Field label={form.discount_type === 'percent' ? 'Percent off' : 'Amount off (₹)'}>
                  <input
                    required
                    type="number"
                    min="1"
                    max={form.discount_type === 'percent' ? 100 : undefined}
                    step="1"
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    className="field-input"
                  />
                </Field>
              </div>

              <Field label="Applies to plan">
                <select
                  value={form.applies_to_plan_slug}
                  onChange={(e) => setForm({ ...form, applies_to_plan_slug: e.target.value })}
                  className="field-input"
                >
                  <option value="all">All membership plans</option>
                  {plans.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.name} — ₹{fmtNum(p.price)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Valid until (optional)">
                <input
                  type="datetime-local"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className="field-input"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry date</p>
              </Field>

              <Field label="Max uses (optional)">
                <input
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="field-input"
                />
              </Field>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded border-gray-300"
                />
                Active immediately
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm rounded-xl ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
                >
                  {saving ? 'Creating…' : 'Create coupon'}
                </button>
              </div>
            </form>
          </div>
          <style>{`
            .field-input {
              width: 100%;
              padding: 0.5rem 0.75rem;
              font-size: 0.875rem;
              border-radius: 0.5rem;
              border: 1px solid #e5e7eb;
            }
            .field-input:focus {
              outline: none;
              border-color: #818cf8;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
            }
          `}</style>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-gray-200">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-gray-900 tabular-nums">{value}</div>
    </div>
  );
}

function StatusBadge({ tone, children }) {
  const cls =
    tone === 'green'
      ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
      : tone === 'red'
        ? 'bg-red-50 text-red-800 ring-red-200'
        : 'bg-gray-100 text-gray-700 ring-gray-200';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${cls}`}>
      {children}
    </span>
  );
}
